import { Button, Empty, Select, Typography, theme } from 'antd';
import dynamic from 'next/dynamic';
import { KeyboardEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GrispiInputProps } from '../..';
import useToggle from '../../../../hooks/useToggle';
import { grispiNotification } from '../../../../store/useNotificationStore';
import { CustomFieldOptionType } from '../../../CustomField/CustomField.type';
import GrispiInputContainer from '../../GrispiInputContainer';

const DynamicOptionModal = dynamic(() => import('../../../AddOptionModal/index'));

type GrispiMultiSelectInputProps = Omit<GrispiInputProps<'MULTI_SELECT'>, 'type'>;

export const getLabelByValue = (value: any, options: any) => {
    const controlLabel = options.find((o) => o.value === value)?.label;
    return controlLabel;
};

export const checkDuplicatedValuesInOption = (options: Omit<CustomFieldOptionType, 'order'>[], field: any) => {
    const newOptions = [] as Omit<CustomFieldOptionType, 'order'>[];
    options?.map((option) => {
        if (!newOptions.find((o) => o.value === option.value)) {
            newOptions.push(option);
        } else {
            console.warn(
                `GrispiMultiSelectInput check field: ${
                    typeof field === 'string' ? field : 'unknown field'
                }, we have duplicated options !!`,
                options
            );
        }
    });
    return options;
};
const transformFormValue = (value: string) => {
    if (!value) return;
    return value.split(',');
};

const GrispiStandardMultiSelectInput = ({
    antdProps,
    containerProps,
    isNewValueAllowed,
    onChange,
    options,
    value,
    fieldKey,
    ...rest
}: GrispiMultiSelectInputProps) => {
    const { t } = useTranslation('forms');
    const {
        token: { colorTextLightSolid }
    } = theme.useToken();
    const [items, setItems] = useState(checkDuplicatedValuesInOption(options, containerProps?.label));
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddable, setIsAddable] = useState(false);
    const [isOptionModalOpen, , openOptionModal, closeOptionModal] = useToggle(false);
    useEffect(() => {
        if (typeof value === 'string') {
            const newValue = transformFormValue(value);
            // console.log(`useEffect , newValue`, newValue);
            if (!newValue) return;
            newValue.map((v) => {
                if (Array.isArray(items) && !items.find((o) => o.value === v)) {
                    setItems([...items, { value: v, label: v } as Omit<CustomFieldOptionType, 'order'>]);
                }
            });
        }
    }, [items, value]);
    const handleSearch = (value: string) => {
        setSearchTerm(value);
        if (isNewValueAllowed && value !== '' && fieldKey) {
            setIsAddable(!items.find((i) => i.label.toString().toLowerCase() === value.toLowerCase()));
        } else {
            setIsAddable(false);
        }
    };
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && isAddable) {
            e.stopPropagation();
            e.preventDefault();
            addItem();
        }
    };
    const resetSearch = () => {
        setSearchTerm('');
        setIsAddable(false);
    };
    const addItem = () => {
        if (!items.find((o) => o.value === searchTerm) && isAddable) {
            openOptionModal();
        } else {
            grispiNotification(
                'warning',
                `you can not add '${searchTerm}', '${searchTerm}' already exists in list as '${getLabelByValue(
                    searchTerm,
                    options
                )}'`,
                'warning'
            );
            setSearchTerm('');
            setIsAddable(false);
        }
    };
    const notFoundContent = (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}>
            {isNewValueAllowed ? (
                <Button type="primary" onClick={addItem} className="full-width">
                    <Typography.Text ellipsis style={{ color: colorTextLightSolid }} className="full-width">
                        {`${t('addOption')} "${searchTerm || searchTerm}"`}
                    </Typography.Text>
                </Button>
            ) : null}
        </Empty>
    );
    return (
        <>
            <GrispiInputContainer {...containerProps}>
                <Select<string | string[]>
                    allowClear
                    showSearch
                    tokenSeparators={[',']} // only applies when mode='tags'
                    {...antdProps}
                    onChange={(v) => onChange(v.toString())}
                    optionFilterProp="label"
                    optionLabelProp="label"
                    options={items}
                    filterOption={(input, option) => {
                        if (typeof option.label === 'string')
                            return option.label.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0;
                        else {
                            return false;
                        }
                    }}
                    value={transformFormValue(value)}
                    notFoundContent={notFoundContent}
                    onDropdownVisibleChange={(_) => {
                        resetSearch();
                    }}
                    onSearch={handleSearch}
                    onSelect={resetSearch}
                    onDeselect={resetSearch}
                    searchValue={searchTerm}
                    mode="multiple"
                    onInputKeyDown={(e) => handleKeyDown(e)}
                    {...rest}
                />
            </GrispiInputContainer>
            {isNewValueAllowed && isOptionModalOpen && (
                <DynamicOptionModal
                    isOpen={isOptionModalOpen}
                    closeModal={closeOptionModal}
                    optionValue={searchTerm}
                    setOptionValue={({ id, label }) => {
                        if (!id || !label) return;
                        setItems([
                            ...items,
                            { value: id?.toString(), label: label } as Omit<CustomFieldOptionType, 'order'>
                        ]);
                        if (value) value = value + ',' + id?.toString();
                        else value = id?.toString();
                        setSearchTerm('');
                        setIsAddable(false);
                        onChange(value);
                    }}
                    fieldKey={fieldKey}
                    fieldName={containerProps.label?.toString()}
                />
            )}
        </>
    );
};

export default GrispiStandardMultiSelectInput;
